import { useEffect, useRef } from "react";
import { soDigitos } from "../../../utils/trocaEmail";

/**
 * Campo de código de verificação com um quadradinho por dígito (padrão 6).
 * Controlado: `valor` é a string de dígitos e `onChange(novoValor)` recebe a nova.
 * Foco automático no primeiro quadradinho, avanço ao digitar, Backspace volta,
 * setas navegam e colar o código inteiro preenche todos.
 * As classes `form-row` e `input-code` herdam o visual dos modais de conta.
 */
export default function CodigoEmail({ valor, onChange, tamanho = 6, invalido = false, rotulo = "Código de verificação", autoFoco = true, desabilitado = false }) {
  const refs = useRef([]);
  const digitos = soDigitos(valor, tamanho).padEnd(tamanho, " ").split("");

  useEffect(() => {
    if (autoFoco && refs.current[0]) refs.current[0].focus();
  }, [autoFoco]);

  function foco(i) {
    const alvo = refs.current[Math.max(0, Math.min(tamanho - 1, i))];
    if (alvo) alvo.focus();
  }

  function montar(lista) {
    return lista.join("").replace(/ /g, "");
  }

  function aoDigitar(i, e) {
    const novo = soDigitos(e.target.value, tamanho);
    if (!novo) {
      const lista = [...digitos]; lista[i] = " ";
      onChange(montar(lista));
      return;
    }
    // vários dígitos de uma vez (autopreenchimento do navegador): espalha a partir de i
    const lista = [...digitos];
    novo.split("").forEach((d, k) => { if (i + k < tamanho) lista[i + k] = d; });
    onChange(montar(lista));
    foco(i + novo.length);
  }

  function aoTeclar(i, e) {
    if (e.key === "Backspace" && !digitos[i].trim()) { e.preventDefault(); foco(i - 1); }
    else if (e.key === "ArrowLeft") { e.preventDefault(); foco(i - 1); }
    else if (e.key === "ArrowRight") { e.preventDefault(); foco(i + 1); }
  }

  function aoColar(e) {
    e.preventDefault();
    const colado = soDigitos(e.clipboardData.getData("text/plain"), tamanho);
    if (!colado) return;
    onChange(colado);
    foco(colado.length >= tamanho ? tamanho - 1 : colado.length);
  }

  return (
    <div className="form-row" role="group" aria-label={rotulo} style={{ gap: 8, flexWrap: "nowrap", margin: "10px 0" }}>
      {digitos.map((d, i) => (
        <input
          key={i}
          ref={(el) => { refs.current[i] = el; }}
          className={`input-code ${invalido ? "input-error" : ""}`}
          style={{ marginRight: 0, width: 44, flex: "none" }}
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          maxLength={tamanho}
          aria-label={`${rotulo}, dígito ${i + 1} de ${tamanho}`}
          value={d.trim()}
          disabled={desabilitado}
          onChange={(e) => aoDigitar(i, e)}
          onKeyDown={(e) => aoTeclar(i, e)}
          onPaste={aoColar}
          onFocus={(e) => e.target.select()}
        />
      ))}
    </div>
  );
}
